import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import { Dialog, DialogContent } from "./ui/dialog";
import {
    Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "./ui/command";
import { Briefcase, Users, Loader2 } from "lucide-react";

const TYPE_LABEL = { buyer: "Buyer", seller: "Seller", intermediary: "Intermediary", investor: "Investor" };

export default function GlobalSearch({ open, onOpenChange }) {
    const [query, setQuery] = useState("");
    const [deals, setDeals] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const reqRef = useRef(0);
    const nav = useNavigate();

    // Reset on close
    useEffect(() => {
        if (!open) {
            setQuery("");
            setDeals([]);
            setContacts([]);
        }
    }, [open]);

    // Debounced fetch (300ms, min 2 chars)
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setDeals([]);
            setContacts([]);
            setLoading(false);
            return;
        }
        const myReq = ++reqRef.current;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const [d, c] = await Promise.all([
                    api.get(`/deals?search=${encodeURIComponent(q)}`),
                    api.get(`/contacts?search=${encodeURIComponent(q)}`),
                ]);
                if (myReq !== reqRef.current) return;
                setDeals(d.data.slice(0, 8));
                setContacts(c.data.slice(0, 8));
            } catch {
                if (myReq !== reqRef.current) return;
                setDeals([]);
                setContacts([]);
            } finally {
                if (myReq === reqRef.current) setLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [query]);

    const go = (path) => {
        onOpenChange(false);
        nav(path);
    };

    const tooShort = query.trim().length > 0 && query.trim().length < 2;
    const hasResults = deals.length > 0 || contacts.length > 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="overflow-hidden p-0 max-w-2xl">
                <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
            <CommandInput
                placeholder="Search deals and contacts…"
                value={query}
                onValueChange={setQuery}
                data-testid="global-search-input"
            />
            <CommandList>
                {loading && (
                    <div className="py-6 flex items-center justify-center text-sm text-muted-foreground gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
                    </div>
                )}
                {!loading && tooShort && (
                    <div className="py-6 text-center text-sm text-muted-foreground">Type at least 2 characters…</div>
                )}
                {!loading && !tooShort && query.trim().length >= 2 && !hasResults && (
                    <CommandEmpty>No deals or contacts match "{query}".</CommandEmpty>
                )}
                {!loading && query.trim().length === 0 && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                        Search across your deals and contacts.
                    </div>
                )}

                {deals.length > 0 && (
                    <CommandGroup heading="Deals">
                        {deals.map((d) => (
                            <CommandItem
                                key={`deal-${d.id}`}
                                value={`deal-${d.id}`}
                                onSelect={() => go(`/deals/${d.id}`)}
                                data-testid="search-result-item"
                                className="cursor-pointer"
                            >
                                <Briefcase className="w-4 h-4 text-navy" strokeWidth={1.5} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="font-medium truncate">{d.company_name}</span>
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono shrink-0">
                                            {d.stage}
                                        </span>
                                    </div>
                                    <div className="text-xs text-muted-foreground font-mono mt-0.5">
                                        {d.sector ? `${d.sector} · ` : ""}{fmtMoney(d.deal_size, d.currency)}
                                    </div>
                                </div>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                )}

                {contacts.length > 0 && (
                    <CommandGroup heading="Contacts">
                        {contacts.map((c) => (
                            <CommandItem
                                key={`contact-${c.id}`}
                                value={`contact-${c.id}`}
                                onSelect={() => go(`/contacts/${c.id}`)}
                                data-testid="search-result-item"
                                className="cursor-pointer"
                            >
                                <Users className="w-4 h-4 text-gold" strokeWidth={1.5} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="font-medium truncate">{c.name}</span>
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono shrink-0">
                                            {TYPE_LABEL[c.type] || c.type}
                                        </span>
                                    </div>
                                    {c.company && <div className="text-xs text-muted-foreground mt-0.5 truncate">{c.company}</div>}
                                </div>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                )}
            </CommandList>
                </Command>
            </DialogContent>
        </Dialog>
    );
}
